import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/environment';

interface IPv4Address {
  id: number;
  ip_address: string;
  proxmox_host_id: number | null;
  vps_instance_id: string | null;
  vps_hostname?: string;
  vps_name?: string;
  status: 'available' | 'allocated' | 'reserved';
  gateway: string;
  netmask: string;
  notes: string | null;
  allocated_at: string | null;
  created_at: string;
}

interface PoolStats {
  total: number;
  available: number;
  allocated: number;
  reserved: number;
}

const IPv4PoolManagement: React.FC = () => {
  const [ips, setIps] = useState<IPv4Address[]>([]);
  const [stats, setStats] = useState<PoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Add IP form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState<'single' | 'cidr'>('single');
  const [newIP, setNewIP] = useState({
    ip_address: '',
    gateway: '15.204.107.254',
    netmask: '255.255.255.255',
    notes: ''
  });
  const [cidrForm, setCidrForm] = useState({
    cidr: '',
    gateway: '15.204.107.254',
    exclude_gateway: ''
  });

  // Reserve form
  const [showReserveForm, setShowReserveForm] = useState(false);
  const [reserveIP, setReserveIP] = useState('');
  const [reserveNotes, setReserveNotes] = useState('');

  useEffect(() => {
    fetchIPPool();
  }, []);

  const fetchIPPool = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch IP pool');
      }

      const data = await response.json();
      if (data.success) {
        setIps(data.data.ips || []);
        setStats(data.data.stats || null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch IP pool');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIP = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newIP)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`IP ${newIP.ip_address} added to pool`);
        setNewIP({ ip_address: '', gateway: '15.204.107.254', netmask: '255.255.255.255', notes: '' });
        setShowAddForm(false);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to add IP');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add IP');
    }
  };

  const handleAddFromCIDR = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool/cidr'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cidrForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Added ${data.data.length} IPs from ${cidrForm.cidr}`);
        setCidrForm({ cidr: '', gateway: '15.204.107.254', exclude_gateway: '' });
        setShowAddForm(false);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to add IPs from CIDR');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add IPs from CIDR');
    }
  };

  const handleDeleteIP = async (id: number, ipAddress: string) => {
    if (!window.confirm(`Delete IP ${ipAddress} from pool?`)) return;

    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/admin/ipv4-pool/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`IP ${ipAddress} deleted from pool`);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to delete IP');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete IP');
    }
  };

  const handleReserve = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool/reserve'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip_address: reserveIP, notes: reserveNotes })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`IP ${reserveIP} reserved`);
        setReserveIP('');
        setReserveNotes('');
        setShowReserveForm(false);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to reserve IP');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reserve IP');
    }
  };

  const handleUnreserve = async (ipAddress: string) => {
    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool/unreserve'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ip_address: ipAddress })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`IP ${ipAddress} is now available`);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to unreserve IP');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to unreserve IP');
    }
  };

  const handleDeallocate = async (vpsInstanceId: string, ipAddress: string) => {
    if (!window.confirm(`Deallocate IP ${ipAddress}? This will remove it from the VPS.`)) return;

    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/admin/ipv4-pool/deallocate'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vps_instance_id: vpsInstanceId })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`IP ${ipAddress} deallocated`);
        fetchIPPool();
      } else {
        setError(data.error || 'Failed to deallocate IP');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to deallocate IP');
    }
  };

  const filteredIPs = filterStatus === 'all'
    ? ips
    : ips.filter(ip => ip.status === filterStatus);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'allocated':
        return 'bg-blue-100 text-blue-800';
      case 'reserved':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">IPv4 Address Pool</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowReserveForm(true)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Reserve IP
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Add IPs
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Total IPs</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.available}</div>
              <div className="text-sm text-gray-500">Available</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.allocated}</div>
              <div className="text-sm text-gray-500">Allocated</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.reserved}</div>
              <div className="text-sm text-gray-500">Reserved</div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button onClick={() => setError('')} className="float-right">&times;</button>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {success}
            <button onClick={() => setSuccess('')} className="float-right">&times;</button>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1 rounded ${filterStatus === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            All ({ips.length})
          </button>
          <button
            onClick={() => setFilterStatus('available')}
            className={`px-3 py-1 rounded ${filterStatus === 'available' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700'}`}
          >
            Available ({ips.filter(ip => ip.status === 'available').length})
          </button>
          <button
            onClick={() => setFilterStatus('allocated')}
            className={`px-3 py-1 rounded ${filterStatus === 'allocated' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'}`}
          >
            Allocated ({ips.filter(ip => ip.status === 'allocated').length})
          </button>
          <button
            onClick={() => setFilterStatus('reserved')}
            className={`px-3 py-1 rounded ${filterStatus === 'reserved' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-700'}`}
          >
            Reserved ({ips.filter(ip => ip.status === 'reserved').length})
          </button>
        </div>

        {/* IP Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIPs.map((ip) => (
                <tr key={ip.id}>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-sm">{ip.ip_address}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-sm text-gray-500">{ip.gateway}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(ip.status)}`}>
                      {ip.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {ip.vps_hostname ? (
                      <div>
                        <div className="font-medium">{ip.vps_hostname}</div>
                        <div className="text-gray-500 text-xs">{ip.vps_name}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{ip.notes || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {ip.status === 'available' && (
                      <button
                        onClick={() => handleDeleteIP(ip.id, ip.ip_address)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    )}
                    {ip.status === 'allocated' && ip.vps_instance_id && (
                      <button
                        onClick={() => handleDeallocate(ip.vps_instance_id!, ip.ip_address)}
                        className="text-orange-600 hover:text-orange-900"
                      >
                        Deallocate
                      </button>
                    )}
                    {ip.status === 'reserved' && (
                      <button
                        onClick={() => handleUnreserve(ip.ip_address)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Unreserve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredIPs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No IP addresses found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add IP Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add IPv4 Addresses</h3>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setAddMode('single')}
                className={`flex-1 px-4 py-2 rounded ${addMode === 'single' ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}
              >
                Single IP
              </button>
              <button
                onClick={() => setAddMode('cidr')}
                className={`flex-1 px-4 py-2 rounded ${addMode === 'cidr' ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}
              >
                CIDR Block
              </button>
            </div>

            {addMode === 'single' ? (
              <form onSubmit={handleAddIP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">IP Address</label>
                  <input
                    type="text"
                    value={newIP.ip_address}
                    onChange={(e) => setNewIP({ ...newIP, ip_address: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                    placeholder="15.204.7.97"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Gateway</label>
                  <input
                    type="text"
                    value={newIP.gateway}
                    onChange={(e) => setNewIP({ ...newIP, gateway: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                  <input
                    type="text"
                    value={newIP.notes}
                    onChange={(e) => setNewIP({ ...newIP, notes: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    Add IP
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleAddFromCIDR} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">CIDR Block</label>
                  <input
                    type="text"
                    value={cidrForm.cidr}
                    onChange={(e) => setCidrForm({ ...cidrForm, cidr: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                    placeholder="15.204.7.96/28"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Network and broadcast addresses will be excluded</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Gateway</label>
                  <input
                    type="text"
                    value={cidrForm.gateway}
                    onChange={(e) => setCidrForm({ ...cidrForm, gateway: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Exclude Gateway IP (optional)</label>
                  <input
                    type="text"
                    value={cidrForm.exclude_gateway}
                    onChange={(e) => setCidrForm({ ...cidrForm, exclude_gateway: e.target.value })}
                    className="mt-1 block w-full border rounded-md px-3 py-2"
                    placeholder="15.204.7.110"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    Add IPs
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Reserve IP Modal */}
      {showReserveForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Reserve IP Address</h3>
            <form onSubmit={handleReserve} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">IP Address to Reserve</label>
                <select
                  value={reserveIP}
                  onChange={(e) => setReserveIP(e.target.value)}
                  className="mt-1 block w-full border rounded-md px-3 py-2"
                  required
                >
                  <option value="">Select an available IP</option>
                  {ips.filter(ip => ip.status === 'available').map(ip => (
                    <option key={ip.id} value={ip.ip_address}>{ip.ip_address}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
                <input
                  type="text"
                  value={reserveNotes}
                  onChange={(e) => setReserveNotes(e.target.value)}
                  className="mt-1 block w-full border rounded-md px-3 py-2"
                  placeholder="Why is this IP reserved?"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowReserveForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg"
                >
                  Reserve
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IPv4PoolManagement;
